import { useMemo } from 'react';
import { PointOnMapType, PointsOnMapFragment, useGuestEventHotelRoomBlocksQuery, useGuestPageContainerBySlugQuery } from '@graphql/generated';
import { DEFAULT_DESTINATION, DEFAULT_RESERVED_ROOM_BLOCKS } from './Custom.constants';

export const useCustomData = (eventId: string, eventHandle: string, pageSlug: string) => {
  const { data: hotelRoomBlocksData, loading: hotelRoomBlocksLoading } = useGuestEventHotelRoomBlocksQuery({
    variables: {
      eventId
    },
    fetchPolicy: 'cache-and-network',
    batchMode: 'fast'
  });
  const reservedRoomBlocks = hotelRoomBlocksData?.event?.hotelRoomBlocks?.reservedRoomBlocks;
  const eventPhoto = hotelRoomBlocksData?.event?.photo?.url ?? '';

  const { data: pageContainerData, loading: pageContainerLoading } = useGuestPageContainerBySlugQuery({
    variables: {
      payload: {
        eventName: eventHandle,
        pageSlug: pageSlug
      }
    },
    fetchPolicy: 'cache-and-network',
    batchMode: 'fast'
  });

  const shortlistingDestination = hotelRoomBlocksData?.event?.hotelRoomBlocks?.destination;
  const travelMapComponent = pageContainerData?.pageContainerBySlug?.components?.find(component => component.__typename === 'TravelMapComponent');
  const blockOrderingInitial = travelMapComponent && travelMapComponent.__typename === 'TravelMapComponent' ? travelMapComponent?.blockOrdering : [];
  const blockOrdering =
    blockOrderingInitial && blockOrderingInitial?.length === reservedRoomBlocks?.length ? blockOrderingInitial : reservedRoomBlocks?.map(roomBlock => roomBlock.id);

  const loading = hotelRoomBlocksLoading || pageContainerLoading;
  const showDefaultData = !loading && (!reservedRoomBlocks || reservedRoomBlocks.length === 0);
  const pointsOnMap = useMemo(() => {
    // Added patch to show shortlisting destination as venue if pointsOnMap is missing and not migrated from editor side
    const venue: PointsOnMapFragment[] = shortlistingDestination
      ? [
          {
            __typename: 'PointsOnMap',
            id: '',
            name: shortlistingDestination.name ?? '',
            address: shortlistingDestination.address ?? '',
            latitude: shortlistingDestination.lat ?? 0,
            longitude: shortlistingDestination.long ?? 0,
            postalCode: shortlistingDestination.postalCode ?? '',
            type: PointOnMapType.venue
          }
        ]
      : [];
    const points =
      travelMapComponent && travelMapComponent.__typename === 'TravelMapComponent'
        ? travelMapComponent?.pointsOnMap?.filter((pointOnMap): pointOnMap is Exclude<PointsOnMapFragment, null> => pointOnMap !== null)
        : venue;
    return points?.length ? points : venue;
  }, [travelMapComponent, shortlistingDestination]);
  const destination = useMemo(() => pointsOnMap?.find(pointOnMap => pointOnMap?.type === PointOnMapType.venue), [pointsOnMap]);
  return {
    loading,
    showDefaultData,
    destination: showDefaultData && !destination ? DEFAULT_DESTINATION : destination,
    eventPhoto,
    reservedRoomBlocks: showDefaultData ? DEFAULT_RESERVED_ROOM_BLOCKS : reservedRoomBlocks,
    blockOrdering,
    pointsOnMap: showDefaultData && pointsOnMap?.length === 0 ? [DEFAULT_DESTINATION] : pointsOnMap
  };
};
